import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserGroupIcon,
  DocumentPlusIcon,
  UserIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  ChatBubbleLeftIcon,
  CalendarIcon,
  InformationCircleIcon,
  BuildingOfficeIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import { 
  useGetStudentsQuery,
  useGetStudentApplicationsQuery,
  useGetConsultantsQuery
} from '../store/api';

const getActivityIcon = (type) => {
  switch (type) {
    case 'new':
      return DocumentPlusIcon;
    case 'success':
      return CheckCircleIcon;
    case 'meeting':
      return CalendarIcon;
    case 'contract':
      return InformationCircleIcon;
    case 'university':
      return UserGroupIcon;
    case 'info':
      return InformationCircleIcon;
    default:
      return ClockIcon;
  }
};

const getActivityColor = (type) => {
  switch (type) {
    case 'new':
      return 'text-blue-500 bg-blue-100';
    case 'success':
      return 'text-green-500 bg-green-100';
    case 'meeting':
      return 'text-purple-500 bg-purple-100';
    case 'contract':
      return 'text-yellow-500 bg-yellow-100';
    case 'university':
      return 'text-indigo-500 bg-indigo-100';
    case 'info':
      return 'text-gray-500 bg-gray-100';
    default:
      return 'text-gray-500 bg-gray-100';
  }
};

export default function Dashboard() {
  const navigate = useNavigate();
  
  const [dashboardStats, setDashboardStats] = useState({
    totalStudents: 0,
    newRequests: 0,
    activeConsultants: 0,
    acceptedStudents: 0
  });

  const [recentActivities, setRecentActivities] = useState([]);
  const [monthlyGrowthData, setMonthlyGrowthData] = useState([]);
  const [weeklyTrendData, setWeeklyTrendData] = useState([65, 45, 78, 52, 91, 73, 88]); // Default values
  const [weeklyDetailedData, setWeeklyDetailedData] = useState([]); // For detailed daily data
  const [loading, setLoading] = useState(true);

  // API queries - get all data with large page size for accurate statistics
  const { data: studentsData, isLoading: studentsLoading, error: studentsError } = useGetStudentsQuery({ per_page: 1000 });
  const { data: applicationsData, isLoading: applicationsLoading, error: applicationsError } = useGetStudentApplicationsQuery({ per_page: 1000 });
  const { data: consultantsData, isLoading: consultantsLoading, error: consultantsError } = useGetConsultantsQuery({ per_page: 1000 });

  useEffect(() => {
    if (!studentsLoading && !applicationsLoading && !consultantsLoading) {
      
      // Handle paginated API responses correctly
      // Students API returns: { status: true, data: { data: [...], total: 12, ... } }
      const students = Array.isArray(studentsData?.data?.data) ? studentsData.data.data : [];
      const studentsTotal = studentsData?.data?.total || students.length;
      
      // Applications might have similar structure
      const applications = Array.isArray(applicationsData?.data?.data) ? applicationsData.data.data : 
                          Array.isArray(applicationsData?.data) ? applicationsData.data : 
                          Array.isArray(applicationsData) ? applicationsData : [];
      const applicationsTotal = applicationsData?.data?.total || applications.length;
      
      // Consultants might have similar structure  
      const consultants = Array.isArray(consultantsData?.data?.data) ? consultantsData.data.data :
                         Array.isArray(consultantsData?.data) ? consultantsData.data : 
                         Array.isArray(consultantsData) ? consultantsData : [];
      const consultantsTotal = consultantsData?.data?.total || consultants.length;

      // Calculate statistics using totals when available, arrays when needed for filtering
      const totalStudents = studentsTotal;
      const activeConsultants = consultants.filter(c => c.is_active === true || c.is_active === 1).length;

      // For filtering operations, we need the actual data arrays (not just totals)
      // Calculate new requests (last 30 days) from available application data
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const newRequests = applications.filter(app => {
        const appDate = new Date(app.created_at || app.first_contact_date);
        return appDate >= thirtyDaysAgo;
      }).length;

      // Calculate accepted students from available application data
      // Status 6 = "Qəbul Edildi" based on the API response
      const acceptedStudents = applications.filter(app => 
        app.status_id === 6 || app.status?.name === 'Qəbul Edildi'
      ).length;

      // Always use real data if we have any
      setDashboardStats({
        totalStudents,
        newRequests,
        activeConsultants,
        acceptedStudents
      });

      // Generate recent activities from real data
      const activities = [];

      // Add recent applications using first_contact_date
      students
        .filter(student => student.first_contact_date)
        .sort((a, b) => new Date(b.first_contact_date) - new Date(a.first_contact_date))
        .slice(0, 3)
        .forEach(student => {
          activities.push({
            id: `student-${student.id}`,
            text: `${student.first_name} ${student.last_name} yeni müraciət etdi`,
            time: formatTimeAgo(student.first_contact_date),
            type: 'new'
          });
        });

      // Add recent successful applications
      students
        .filter(student => student.status_id === 6 || student.status?.name === 'Qəbul Edildi')
        .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
        .slice(0, 2)
        .forEach(student => {
          activities.push({
            id: `success-${student.id}`,
            text: `${student.first_name} ${student.last_name} qəbul aldı`,
            time: formatTimeAgo(student.updated_at || student.created_at),
            type: 'success'
          });
        });

      // Sort all activities by time and limit to 6
      setRecentActivities(
        activities
          .sort((a, b) => new Date(b.time) - new Date(a.time))
          .slice(0, 6)
      );

      // Calculate monthly growth statistics from real data
      const monthlyGrowth = calculateMonthlyGrowth(students);
      setMonthlyGrowthData(monthlyGrowth);

      // Calculate weekly trend data from real student registrations
      const { percentages, detailedData } = calculateWeeklyTrend(students);
      setWeeklyTrendData(percentages);
      setWeeklyDetailedData(detailedData);

      setLoading(false);
    }
  }, [studentsData, applicationsData, consultantsData,
      studentsLoading, applicationsLoading, consultantsLoading]);

  const calculateMonthlyGrowth = (students) => {
    if (!students || students.length === 0) {
      return [];
    }

    const months = [
      'Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'İyun',
      'İyul', 'Avqust', 'Sentyabr', 'Oktyabr', 'Noyabr', 'Dekabr'
    ];
    
    const now = new Date();
    const monthlyData = [];
    const monthlyCounts = [];
    
    // First pass: calculate all monthly counts for last 6 months
    for (let i = 5; i >= 0; i--) {
      const targetDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      
      const monthlyCount = students.filter(student => {
        const studentDate = new Date(student.created_at);
        return studentDate.getMonth() === targetDate.getMonth() && 
               studentDate.getFullYear() === targetDate.getFullYear();
      }).length;
      
      monthlyCounts.push(monthlyCount);
    }
    
    // Find maximum for percentage calculation
    const maxCount = Math.max(...monthlyCounts, 1); // At least 1 to avoid division by zero
    
    // Second pass: create display data
    for (let i = 5; i >= 0; i--) {
      const targetDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = months[targetDate.getMonth()];
      const monthlyCount = monthlyCounts[5 - i];
      
      // Calculate percentage relative to max count
      const percentage = (monthlyCount / maxCount) * 100;
      
      monthlyData.push({
        month: monthName,
        value: Math.max(percentage, 3), // Minimum 3% for visual representation
        count: monthlyCount,
        color: `bg-${['blue', 'green', 'purple', 'red', 'yellow', 'indigo'][i % 6]}-500`,
        fullDate: `${monthName} ${targetDate.getFullYear()}`
      });
    }
    
    return monthlyData;
  };

  const calculateWeeklyTrend = (students) => {
    if (!students || students.length === 0) {
      // Return empty detailed data for loading state
      return { 
        percentages: [10, 10, 10, 10, 10, 10, 10], 
        detailedData: [] 
      };
    }

    const weekData = [];
    const now = new Date();
    // Azerbaijani day names: Sunday=0, Monday=1, Tuesday=2, Wednesday=3, Thursday=4, Friday=5, Saturday=6
    const dayNames = ['B', 'B.e', 'Ç.a', 'Ç', 'C.a', 'C', 'Ş'];
    
    // Get last 7 days
    for (let i = 6; i >= 0; i--) {
      const targetDate = new Date(now);
      targetDate.setDate(now.getDate() - i);
      
      const dailyCount = students.filter(student => {
        const studentDate = new Date(student.created_at);
        return studentDate.toDateString() === targetDate.toDateString();
      }).length;
      
      // Get the actual day of the week for the target date
      const actualDayOfWeek = targetDate.getDay(); // 0=Sunday, 1=Monday, etc.
      
      weekData.push({
        day: dayNames[actualDayOfWeek],
        count: dailyCount,
        date: targetDate.toLocaleDateString('az-AZ', { 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric' 
        })
      });
    }
    
    // Convert to percentages for visual representation
    const maxDaily = Math.max(...weekData.map(d => d.count), 1);
    const percentages = weekData.map(dayData => {
      const percentage = (dayData.count / maxDaily) * 100;
      return Math.max(percentage, 5); // Minimum 5% for visual representation
    });
    
    return { percentages, detailedData: weekData };
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes} dəqiqə əvvəl`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} saat əvvəl`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `${days} gün əvvəl`;
    }
  };

  const statsCards = [
    {
      name: 'Ümumi Tələbələr',
      value: dashboardStats.totalStudents,
      icon: UserGroupIcon,
      change: '+4.75%',
      changeType: 'increase',
      color: 'blue'
    },
    {
      name: 'Yeni Müraciətlər',
      value: dashboardStats.newRequests,
      icon: DocumentPlusIcon,
      change: '+54.02%',
      changeType: 'increase',
      color: 'green'
    },
    {
      name: 'Aktiv Konsultantlar',
      value: dashboardStats.activeConsultants,
      icon: UserIcon,
      change: '0%',
      changeType: 'neutral',
      color: 'purple'
    },
    {
      name: 'Qəbul Alanlar',
      value: dashboardStats.acceptedStudents,
      icon: CheckCircleIcon,
      change: '+19.5%',
      changeType: 'increase',
      color: 'emerald'
    }
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="border-b border-gray-200 pb-4">
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600">
            Statistikalar yüklənir...
          </p>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white shadow rounded-lg p-5 animate-pulse">
              <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-gray-300 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Check if there are API errors
  const hasApiErrors = studentsError || applicationsError || consultantsError;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-600">
          Son fəaliyyətlər və statistik göstəricilər
        </p>
        {hasApiErrors && (
          <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-700">
              ⚠️ API ilə əlaqə problemi var. Console-da ətraflı məlumat üçün F12 basın.
            </p>
          </div>
        )}
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {statsCards.map((item, index) => {
          const gradients = [
            'bg-gradient-to-br from-blue-500 to-blue-600',
            'bg-gradient-to-br from-green-500 to-green-600', 
            'bg-gradient-to-br from-purple-500 to-purple-600',
            'bg-gradient-to-br from-emerald-500 to-emerald-600'
          ];
          return (
            <div key={item.name} className={`${gradients[index]} overflow-hidden shadow-lg rounded-lg transform hover:scale-105 transition-transform duration-200`}>
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <item.icon className="h-8 w-8 text-white" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-blue-100 truncate">
                        {item.name}
                      </dt>
                      <dd className="flex items-baseline">
                        <div className="text-3xl font-bold text-white">
                          {item.value}
                        </div>
                        <div className="ml-2 flex items-baseline text-sm font-semibold text-blue-100">
                          {item.changeType === 'increase' && (
                            <ArrowTrendingUpIcon className="self-center flex-shrink-0 h-4 w-4 text-white mr-1" />
                          )}
                          <span className="sr-only">
                            {item.changeType === 'increase' ? 'Artıb' : item.changeType === 'decrease' ? 'Azalıb' : 'Sabit'}
                          </span>
                          {item.change}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
              <div className="bg-black bg-opacity-10 px-5 py-3">
                <div className="text-xs text-blue-100">
                  {item.changeType === 'increase' ? '↗ Artım' : item.changeType === 'decrease' ? '↘ Azalma' : '→ Sabit'}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent activities and quick actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activities */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Son Fəaliyyətlər
            </h3>
            <div className="flow-root">
              <ul role="list" className="-mb-8">
                {recentActivities.length > 0 ? recentActivities.map((activity, activityIdx) => {
                  const Icon = getActivityIcon(activity.type);
                  return (
                    <li key={activity.id}>
                      <div className="relative pb-8">
                        {activityIdx !== recentActivities.length - 1 ? (
                          <span
                            className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                            aria-hidden="true"
                          />
                        ) : null}
                        <div className="relative flex space-x-3">
                          <div>
                            <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${getActivityColor(activity.type)}`}>
                              <Icon className="h-4 w-4" />
                            </span>
                          </div>
                          <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                            <div>
                              <p className="text-sm text-gray-500">{activity.text}</p>
                            </div>
                            <div className="text-right text-sm whitespace-nowrap text-gray-500">
                              <time>{activity.time}</time>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                }) : (
                  <li className="text-center text-gray-500 py-4">
                    Hələ ki fəaliyyət yoxdur
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Tez Fəaliyyətlər
            </h3>
            <div className="space-y-3">
              <button 
                onClick={() => navigate('/student-requests')}
                className="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <span className="flex items-center">
                  <DocumentPlusIcon className="h-5 w-5 text-blue-500 mr-3" />
                  Yeni Tələbə Müraciəti
                </span>
                <span className="text-gray-400">→</span>
              </button>
              
              <button 
                onClick={() => navigate('/consultants')}
                className="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <span className="flex items-center">
                  <UserIcon className="h-5 w-5 text-green-500 mr-3" />
                  Yeni Konsultant Əlavə Et
                </span>
                <span className="text-gray-400">→</span>
              </button>
              
              <button 
                onClick={() => navigate('/universities')}
                className="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <span className="flex items-center">
                  <BuildingOfficeIcon className="h-5 w-5 text-purple-500 mr-3" />
                  Universitet Əlavə Et
                </span>
                <span className="text-gray-400">→</span>
              </button>
              
              <button 
                onClick={() => navigate('/countries')}
                className="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <span className="flex items-center">
                  <GlobeAltIcon className="h-5 w-5 text-orange-500 mr-3" />
                  Ölkə Əlavə Et
                </span>
                <span className="text-gray-400">→</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Analytics and Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Growth Bar Chart */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Aylıq Artım Statistikası (Son 6 ay)
            </h3>
            <div className="space-y-4">
              {monthlyGrowthData.length > 0 ? (
                monthlyGrowthData.map((item) => (
                  <div key={item.month} className="flex items-center space-x-3">
                    <div className="w-16 text-sm font-medium text-gray-700">{item.month}</div>
                    <div className="flex-1 bg-gray-200 rounded-full h-3">
                      <div 
                        className={`h-3 rounded-full ${item.color} transition-all duration-1000`}
                        style={{ width: `${item.value}%` }}
                        title={`${item.fullDate}: ${item.count} tələbə qeydiyyatdan keçib`}
                      ></div>
                    </div>
                    <div className="w-16 text-sm font-semibold text-gray-900">
                      {item.count} nəfər
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-gray-500 py-4">
                  <p className="text-sm">Aylıq məlumatlar yüklənir...</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Pie Chart Representation */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Tələbə Bölgüsü
            </h3>
            <div className="flex justify-center mb-6">
              <div className="relative w-40 h-40">
                <svg className="w-40 h-40 transform -rotate-90" viewBox="0 0 144 144">
                  <circle cx="72" cy="72" r="60" fill="none" stroke="#e5e7eb" strokeWidth="12"/>
                  <circle cx="72" cy="72" r="60" fill="none" stroke="#3b82f6" strokeWidth="12"
                    strokeDasharray="188.4" strokeDashoffset="47.1" className="transition-all duration-1000"/>
                  <circle cx="72" cy="72" r="60" fill="none" stroke="#10b981" strokeWidth="12"
                    strokeDasharray="188.4" strokeDashoffset="94.2" className="transition-all duration-1000"/>
                  <circle cx="72" cy="72" r="60" fill="none" stroke="#f59e0b" strokeWidth="12"
                    strokeDasharray="188.4" strokeDashoffset="141.3" className="transition-all duration-1000"/>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{dashboardStats.totalStudents}</div>
                    <div className="text-xs text-gray-500">Ümumi</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Yeni Müraciətlər</span>
                </div>
                <span className="text-sm font-semibold">{dashboardStats.newRequests}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Qəbul Alanlar</span>
                </div>
                <span className="text-sm font-semibold">{dashboardStats.acceptedStudents}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Prosesdə Olanlar</span>
                </div>
                <span className="text-sm font-semibold">
                  {Math.max(0, dashboardStats.totalStudents - dashboardStats.acceptedStudents - dashboardStats.newRequests)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">
            Performans Göstəriciləri
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Line Chart Simulation */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-700">Həftəlik Trend (Son 7 gün)</h4>
              <div className="h-32 flex items-end space-x-1">
                {weeklyTrendData.map((height, index) => (
                  <div 
                    key={index} 
                    className="flex-1 bg-gradient-to-t from-blue-500 to-blue-300 rounded-t transition-all duration-1000 cursor-pointer"
                    style={{ height: `${height}%` }}
                    title={weeklyDetailedData[index] ? 
                      `${weeklyDetailedData[index].date}: ${weeklyDetailedData[index].count} tələbə qeydiyyatdan keçib` : 
                      'Məlumat yüklənir...'}
                  ></div>
                ))}
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                {weeklyDetailedData.length > 0 ? 
                  weeklyDetailedData.map((dayData, index) => (
                    <span key={index} title={`${dayData.date}: ${dayData.count} tələbə`}>
                      {dayData.day}
                    </span>
                  )) :
                  ['B', 'B.e', 'Ç.a', 'Ç', 'C.a', 'C', 'Ş'].map((day, index) => (
                    <span key={index}>{day}</span>
                  ))
                }
              </div>
              {weeklyDetailedData.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>
                      Bu həftə ümumi: <strong>{weeklyDetailedData.reduce((sum, day) => sum + day.count, 0)} tələbə</strong>
                    </span>
                    <span>
                      Gündəlik ortalama: <strong>{(weeklyDetailedData.reduce((sum, day) => sum + day.count, 0) / 7).toFixed(1)}</strong>
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Radial Progress */}
            <div className="flex flex-col items-center space-y-4">
              <h4 className="text-sm font-medium text-gray-700">Məqsəd Yerinə Yetirmə</h4>
              <div className="relative w-24 h-24">
                <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e7eb" strokeWidth="8"/>
                  <circle cx="50" cy="50" r="40" fill="none" stroke="#10b981" strokeWidth="8"
                    strokeDasharray="251.2" 
                    strokeDashoffset={`${251.2 - (251.2 * (dashboardStats.totalStudents > 0 ? 
                      (dashboardStats.acceptedStudents / dashboardStats.totalStudents) * 100 : 0) / 100)}`} 
                    className="transition-all duration-2000"/>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xl font-bold text-green-600">
                    {dashboardStats.totalStudents > 0 ? Math.round((dashboardStats.acceptedStudents / dashboardStats.totalStudents) * 100) : 0}%
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-500 text-center">Qəbul nisbəti</p>
            </div>

            {/* Activity Gauge */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-700">Aktivlik Səviyyəsi</h4>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Müraciətlər</span>
                    <span className="font-medium">
                      {dashboardStats.totalStudents > 0 ? Math.round((dashboardStats.newRequests / dashboardStats.totalStudents) * 100) : 0}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full transition-all duration-1000" 
                         style={{width: `${dashboardStats.totalStudents > 0 ? (dashboardStats.newRequests / dashboardStats.totalStudents) * 100 : 0}%`}}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Qəbullar</span>
                    <span className="font-medium">
                      {dashboardStats.totalStudents > 0 ? Math.round((dashboardStats.acceptedStudents / dashboardStats.totalStudents) * 100) : 0}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-green-600 h-2 rounded-full transition-all duration-1000" 
                         style={{width: `${dashboardStats.totalStudents > 0 ? (dashboardStats.acceptedStudents / dashboardStats.totalStudents) * 100 : 0}%`}}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Konsultant Effektivliyi</span>
                    <span className="font-medium">
                      {dashboardStats.activeConsultants > 0 ? 
                        `${(dashboardStats.acceptedStudents / dashboardStats.activeConsultants).toFixed(1)}/konsultant` : '0/konsultant'}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-purple-600 h-2 rounded-full transition-all duration-1000" 
                         style={{width: `${dashboardStats.activeConsultants > 0 ? 
                           Math.min((dashboardStats.acceptedStudents / dashboardStats.activeConsultants) * 20, 100) : 0}%`}}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 